package macha.sh.kotlin.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.CloseIcon
import com.varabyte.kobweb.silk.components.icons.HamburgerIcon
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import macha.sh.kotlin.SiteColors
import macha.sh.kotlin.SiteModifiers
import macha.sh.kotlin.components.layouts.SiteContentMaxWidth
import macha.sh.kotlin.components.widgets.IconButton
import macha.sh.kotlin.integrations.PlayMode
import macha.sh.kotlin.integrations.powerGlitch
import macha.sh.kotlin.SiteTypography
import macha.sh.kotlin.applyFont
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

@Composable
private fun NavLink(path: String, text: String) {
    var isHovered by remember { mutableStateOf(false) }
    val palette = SiteColors
    Link(
        path,
        text,
        modifier = Modifier
            .color(if (isHovered) palette.Hover else palette.SiteText)
            .onMouseEnter { isHovered = true }
            .onMouseLeave { isHovered = false },
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)
    )
}

@Composable
private fun MenuItems() {
    NavLink("/", "[ Home ]")
    NavLink("/blog-guides", "[ Blog & Guides ]")
    NavLink("/repos", "[ Repos ]")
    NavLink("/about", "[ About ]")
    NavLink("/contact", "[ Contact ]")
}

object NaviLayout {
    private val Mods = SiteModifiers.Navi
    @Composable
    fun Content() {
        Column(Modifier.fillMaxWidth().gap(0.75.cssRem), horizontalAlignment = Alignment.CenterHorizontally) {
            Banner.Content()
            Nav(attrs = { attr("aria-label", "Primary navigation") }) {
                NavHeader.Content()
            }
        }
    }
}

object NavHeader {
    private val Mods = SiteModifiers.Navi
    @Composable
    fun Content() {
        Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
            Box(modifier = Mods.naviContainer()) {
                Row(modifier = Mods.navi(), verticalAlignment = Alignment.CenterVertically) {
                    Row(modifier = Mods.naviItems()) { MenuItems() }
                    Row(modifier = Mods.hamburger(), verticalAlignment = Alignment.CenterVertically) { 
                        var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }
                        HamburgerButton(onClick =  { menuState = SideMenuState.OPEN })
                        if (menuState != SideMenuState.CLOSED) { SideMenu (
                            menuState,
                            close = { menuState = menuState.close() },
                            onAnimationEnd = { if (menuState == SideMenuState.CLOSING) menuState = SideMenuState.CLOSED })
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun HamburgerButton(onClick: () -> Unit) {
    IconButton("Open navigation menu", onClick) { HamburgerIcon() }
}
@Composable
private fun CloseButton(onClick: () -> Unit) {
    IconButton("Close navigation menu", onClick) { CloseIcon() }
}

val SideMenuSlideInAnim = Keyframes { from { Modifier.translateX(100.percent) } to { Modifier } }
// abruptly). Instead, we start animating it out and only stop rendering it when the animation is complete.
enum class SideMenuState {
    CLOSED,
    OPEN,
    CLOSING;
    fun close() = when (this) {
        CLOSED -> CLOSED
        OPEN -> CLOSING
        CLOSING -> CLOSING
    }
}

@Composable
private fun SideMenu(menuState: SideMenuState, close: () -> Unit, onAnimationEnd: () -> Unit) {
    Overlay( Modifier
        .setVariable(OverlayVars.BackgroundColor, Colors.Transparent)
        .onClick { close() }) {
        key(menuState) { // Force recompute animation parameters when close button is clicked
            Column( Modifier
                .fillMaxHeight()
                .width(clamp(8.cssRem, 33.percent, 10.cssRem))
                .align(Alignment.CenterEnd)
                // Close button will appear roughly over the hamburger button, so the user can close
                // things without moving their finger / cursor much.
                .padding(top = 1.cssRem, leftRight = 1.cssRem)
                .gap(1.5.cssRem)
                .backgroundColor(SiteColors.BoxBkg)
                .animation( SideMenuSlideInAnim.toAnimation(
                    duration = 200.ms,
                    timingFunction = if (menuState == SideMenuState.OPEN) AnimationTimingFunction.EaseOut else AnimationTimingFunction.EaseIn,
                    direction = if (menuState == SideMenuState.OPEN) AnimationDirection.Normal else AnimationDirection.Reverse,
                    fillMode = AnimationFillMode.Forwards
                ))
                .borderRadius(topLeft = 2.cssRem)
                .onClick { it.stopPropagation() }
                .onAnimationEnd { onAnimationEnd() },
                horizontalAlignment = Alignment.End 
            ) {
                CloseButton(onClick = { close() })
                Column(Modifier.padding(right = 0.75.cssRem).gap(1.5.cssRem).fontSize(1.4.cssRem), horizontalAlignment = Alignment.End) {
                    MenuItems()
                }
            }
        }
    }
}
